import { Grid, Theme, Typography, makeStyles } from '@material-ui/core';
import {
  CoverageCatalog,
  CoverageCatalogResume,
  Quotation,
} from 'data-layer-cr/dist/models';
import {
  DefaultButton,
  DefaultCard,
  MessageBox,
  PhotoCard,
  RadioButtonGroup,
  colors,
} from 'design-system';
import { useMemo, useState } from 'react';
import { ConnectedProps, connect } from 'react-redux';
import {
  ISQUOTATION_CORRECT,
  VERIFY_QUOTATION_STEP,
  trackCurrentStep,
  trackEvent,
} from 'src/analytics';
import { RootState } from 'src/store/rootReducer';

// resources
import CarIcon from 'src/components/Icons/CarIcon';
import booleanRadioData from 'src/constants/booleanRadioData';

// subcomponents
import {
  setCarInvoiceImage,
  setUploadStateMap,
  uploadSingleFile,
} from 'src/store/inspectionForm/actions';
import { QuotationSummaryModal } from './components';

const useStyles = makeStyles((theme: Theme) => ({
  infoCard: {
    position: 'relative',
  },
  confirmationCard: {
    position: 'relative',
  },
  carIcon: {
    position: 'absolute',
    right: theme.spacing(5),
  },
  carModelText: {
    color: colors.abbey,
    fontSize: 16,
  },
  textArea: {
    marginBottom: theme.spacing(2),
  },
  cardActions: {
    marginBottom: theme.spacing(-2),
  },
  dotted: {
    border: 0,
    height: 0,
    borderTop: `1px dashed ${colors.silver}`,
  },
  confirmationAction: {
    position: 'relative',
  },
  nextButton: {
    maxWidth: theme.spacing(30),
    marginLeft: 'auto',
    marginRight: 'auto',
    marginBottom: 10,
  },
  paddedText: {
    display: 'inline-block',
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(2),
  },
}));
export const hintMessage =
  'antes de avanzar con la inspección revisa las opciones presentadas en la cotización.';

export const errorMessage =
  'Por favor contacta a tu Corredor o a un Ejecutivo Comercial de ASSA al  800-800-ASSA (2772) para recibir asistencia.';

export const CAR_INVOICE_IDENTIFIER = 'car-invoice-image';

/**
 * Helper function to extract customer's first name
 * when customer is natural person or fullName if legal person
 */
export function parseCustomerName(quotation: Quotation): {
  [key: string]: string;
} {
  const { customerName } = quotation.summary;
  const { firstName } = quotation.customer;
  const { customerType } = quotation;

  return {
    promptName: customerType === 'N' ? firstName : customerName,
    fullName: customerName,
  };
}

export const ViewQuotationSummary = ({
  fullName,
  modelBrand,
  quotation,
  setShowModal,
}: {
  fullName: string;
  modelBrand: string;
  quotation: Quotation;
  setShowModal: (show: boolean) => void;
}): JSX.Element => {
  const classes = useStyles();
  return (
    <Grid item xs={12} className={classes.infoCard} data-testid="car-info">
      <DefaultCard>
        <Grid container>
          <div className={classes.carIcon}>
            <CarIcon />
          </div>
          <Grid item xs={12} className={classes.textArea}>
            <Grid item xs={12}>
              <Typography data-testid="customer-name" variant="h3">
                {fullName}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography
                data-testid="car-details"
                variant="h3"
                className={classes.carModelText}
              >
                {modelBrand} / {quotation.summary.carYear}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography
                data-testid="coverage-description"
                variant="subtitle1"
              >
                {quotation.summary.description}
              </Typography>
            </Grid>
          </Grid>

          {/* Card Action Area */}
          <Grid
            item
            xs={12}
            container
            justifyContent="center"
            className={classes.cardActions}
          >
            <Grid item xs={12}>
              <hr className={classes.dotted} />
            </Grid>
            <DefaultButton
              data-testid="modal-button"
              variant="borderless"
              onClick={(): void => setShowModal(true)}
            >
              VER COTIZACIÓN
            </DefaultButton>
          </Grid>
        </Grid>
      </DefaultCard>
    </Grid>
  );
};

/**
 * For @maxAllowedPayments , it should be a refactor where, the amount of payments
 * should be on the quotation, instead on redux.
 */
export const QuotationSummary = ({
  quotation,
  coverageCatalog,
  coverageCatalogResumes,
  maxAllowedPayments,
  onSubmit,
  carInvoiceFilename,
  carInvoiceBase64,
  setCarInvoiceImageAction,
  setUploadStateMapAction,
  uploadInvoiceAction,
}: Props): JSX.Element => {
  const classes = useStyles();
  const [showModal, setShowModal] = useState<boolean>(false);
  const [confirmed, setConfirmed] = useState<boolean | undefined>(undefined);

  // to allow for initial "non" chosen option
  const booleanValue = useMemo(() => {
    if (confirmed === undefined) {
      return '';
    }
    if (!confirmed) {
      return 'no';
    }
    return 'yes';
  }, [confirmed]);

  // only show message when user selects "no"
  const showErrorMessage = useMemo(
    () => (confirmed === undefined || confirmed ? '' : errorMessage),
    [confirmed],
  );

  /** Parse display data */
  const { promptName, fullName } = useMemo(
    () => parseCustomerName(quotation),
    [quotation],
  );
  const promptMessage = useMemo(
    () => `${promptName}, ${hintMessage}`,
    [promptName],
  );
  const modelBrand = useMemo(
    () => `${quotation.summary.carBrand} ${quotation.summary.carModel}`,
    [quotation],
  );

  function handleCarInvoiceFileCaptured(
    fileName: string,
    base64Image: string,
  ): void {
    if (base64Image !== '') {
      uploadInvoiceAction('invoice', base64Image);
    }
    setCarInvoiceImageAction(fileName, base64Image);
    setUploadStateMapAction({ [CAR_INVOICE_IDENTIFIER]: 'initial' });
  }

  return (
    <Grid container spacing={2}>
      <QuotationSummaryModal
        data-testid="modal"
        open={showModal}
        quotation={quotation}
        coverageCatalog={coverageCatalog}
        coverageCatalogResumes={coverageCatalogResumes}
        maxAllowedPayments={maxAllowedPayments}
        onCloseRequested={(): void => setShowModal(false)}
      />

      {/* Prompt message area */}
      <Grid item xs={12}>
        <MessageBox
          data-testid="prompt-message"
          message={promptMessage}
          variant="info"
          hideCloseButton
        />
      </Grid>

      <ViewQuotationSummary
        fullName={fullName}
        modelBrand={modelBrand}
        quotation={quotation}
        setShowModal={setShowModal}
      />

      {/* Confirmation Card */}
      <Grid item xs={12} className={classes.confirmationCard}>
        <DefaultCard>
          <Grid item xs={12}>
            <Typography variant="body2">
              ¿La cotización presentada es la correcta?
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <RadioButtonGroup
              inputId="sales-web-inspection-radio-confirmation"
              radioGroupName="confirmation"
              formTitle=""
              defaultValue={booleanValue}
              radioData={booleanRadioData}
              onChange={(newVal): void => {
                setConfirmed(newVal.Value === 'yes');
              }}
              errorMessage={showErrorMessage}
              centerLabels
            />
          </Grid>
        </DefaultCard>
      </Grid>
      {quotation.units[0].unit.hasBill && (
        <Grid item xs={12}>
          <PhotoCard
            id="sales-web-inspection-button-carInvoice"
            data-testid="carInvoice-card"
            title="Factura de compra (opcional)"
            subTitle="Incluya una foto la factura de compra del auto completa para exonerar la inspección"
            fileName={carInvoiceFilename}
            base64String={carInvoiceBase64}
            onLocalImageCaptured={handleCarInvoiceFileCaptured}
            confirmationMessage="¿Estás seguro que deseas eliminar el documento?"
          />
        </Grid>
      )}
      <Grid
        item
        xs={12}
        container
        justifyContent="center"
        className={classes.nextButton}
      >
        <DefaultButton
          id="sales-web-inspection-button-next"
          data-testid="next-button"
          onClick={(): void => {
            trackEvent({
              eventName: VERIFY_QUOTATION_STEP,
              eventProperties: {
                [ISQUOTATION_CORRECT]: 'yes',
              },
            });
            trackCurrentStep(VERIFY_QUOTATION_STEP);
            onSubmit();
          }}
          disabled={!confirmed}
          fullWidth
        >
          Siguiente
        </DefaultButton>
      </Grid>
    </Grid>
  );
};

export const mapStateToProps = (
  state: RootState,
): {
  quotation: Quotation;
  coverageCatalog: CoverageCatalog[];
  coverageCatalogResumes: CoverageCatalogResume[];
  maxAllowedPayments: number;
  carInvoiceFilename: string;
  carInvoiceBase64: string;
} => ({
  quotation: state.quotation.quotation,
  coverageCatalog: state.coverageCatalog.coverageCatalogs,
  coverageCatalogResumes: state.coverageCatalog.coverageCatalogResumes,
  maxAllowedPayments: state.emissionPolicy.paymentFields.paymentQuantity,
  carInvoiceFilename: state.inspectionForm.carInvoiceFilename,
  carInvoiceBase64: state.inspectionForm.carInvoiceBase64,
});

const reduxActions = {
  setCarInvoiceImageAction: setCarInvoiceImage,
  setUploadStateMapAction: setUploadStateMap,
  uploadInvoiceAction: uploadSingleFile,
};
const connector = connect(mapStateToProps, reduxActions);
type PropsFromRedux = ConnectedProps<typeof connector>;

interface Props extends PropsFromRedux {
  onSubmit: () => void;
}

export default connector(QuotationSummary);
